import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import type { Distributor } from "@shared/types/Distributor";
import type {
  Category,
  Container,
  ProductType,
  QuickOrderListProduct,
} from "@shared/types/ProductLine";
import type {
  QuickOrderList,
  QuickOrderListDetail,
  QuickOrderListFilters,
  QuickOrderListIndex,
} from "@shared/types/QuickOrderList";

type QuickOrderListIndexParams = {};

type QuickOrderListDetailParams = {
  show_all_products?: boolean;
};

type QuickOrderListProductsParams = {
  page?: number;
  direction?: string;
  sort?: string;
};

const hasDistributorAndInventoryId = (
  listProduct: QuickOrderListProduct,
  distributorId: number,
): boolean => {
  return (
    listProduct.product?.distributor_id === distributorId && !!listProduct.product?.inventory_id
  );
};

export const getInventoryIdsPerDistributor = (
  quickOrderListProducts: QuickOrderListProduct[],
  distributorIds: number[],
): { distributorId: number; inventoryIds: number[] }[] => {
  const distributorInventoryIds = [];
  distributorIds.forEach(distributorId => {
    const inventoryIds = quickOrderListProducts
      .filter(listProduct => hasDistributorAndInventoryId(listProduct, distributorId))
      .map(listProduct => listProduct?.product?.inventory_id);
    if (inventoryIds.length) {
      const inventoryIdsByDistributor = {
        distributorId: distributorId,
        inventoryIds: inventoryIds,
      };
      distributorInventoryIds.push(inventoryIdsByDistributor);
    }
  });
  return distributorInventoryIds;
};

export const getQuickOrderListIndex = async (
  params: QuickOrderListIndexParams = null,
): Promise<QuickOrderListIndex[]> => {
  try {
    return await HttpPromise({
      data: params,
      method: "GET",
      url: "/api/retailer/quick_order_lists",
    });
  } catch (e) {
    return [];
  }
};

export const getQuickOrderListProducts = async (
  params: QuickOrderListProductsParams = null,
): Promise<QuickOrderListProduct[]> => {
  try {
    return await HttpPromise({
      data: params,
      method: "GET",
      url: "/api/retailer/quick_order_list_products",
    });
  } catch (e) {
    return [];
  }
};

export const getQuickOrderListDetail = async (
  listId: number,
  params: QuickOrderListDetailParams = null,
): Promise<QuickOrderListDetail> => {
  try {
    return await HttpPromise({
      data: params,
      method: "GET",
      url: `/api/retailer/quick_order_lists/${listId}`,
    });
  } catch (e) {
    console.error(e);

    return <QuickOrderListDetail>{};
  }
};

export const prepareFilters = (filters: {
  smartFilterDistributors?: Distributor[];
  distributors: Distributor[];
  productTypes: {
    containers: Container[];
    categories: Category[];
    types: ProductType[];
  };
}): QuickOrderListFilters => {
  return {
    smartFilterDistributors: filters.smartFilterDistributors,
    distributors: filters.distributors,
    productTypes: filters.productTypes.types,
    containers: filters.productTypes.containers.map(container => container.container_type),
    categories: filters.productTypes.categories,
    subcategories: [],
  };
};

export const getPreviouslyOrderedList = async (): Promise<QuickOrderList> => {
  try {
    return await HttpPromise({
      method: "GET",
      url: "/api/retailer/quick_order_lists/previously_ordered_list",
    });
  } catch (e) {
    console.error(e);

    return null;
  }
};
